import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult } from 'mongodb';

/**
 * @entity MatchComment
 */
@Entity()
export class MatchComment extends AdornisEntity {
  static override _class = 'MatchComment';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(type => String)
  text: Maybe<string>;

  @Field(type => Date)
  createdAt: Maybe<Date>;

  @Field(type => Boolean)
  isAlert: Maybe<boolean>;
}

/**
 * @entity Match
 */
@Entity()
export class Match extends MongoEntity {
  static override _class = 'Match';
  static override _collectionName = 'match';

  constructor(doc: PartialEntityData<Match>) {
    super(doc);
  }

  @Field(type => ID)
  mentorID: Maybe<string>;

  @Field(type => BuddyUserMentor, {
    resolve: function (this: Match) {
      return async () => {
        if (this.mentor?._id && baseqlMetaData.entities.validSubclasses(BuddyUserMentor).includes(this.mentor?._class))
          return this.mentor;
        const mentor = this.mentorID ? await loadMongoData(BuddyUserMentor, this.mentorID) : null;
        return mentor as BuddyUserMentor;
      };
    },
  })
  mentor: Maybe<BuddyUserMentor>;

  @Field(type => ID)
  menteeID: Maybe<string>;

  @Field(type => BuddyUserMentee, {
    resolve: function (this: Match) {
      return async () => {
        if (this.mentee?._id && baseqlMetaData.entities.validSubclasses(BuddyUserMentee).includes(this.mentee?._class))
          return this.mentee;
        const mentee = this.menteeID ? await loadMongoData(BuddyUserMentee, this.menteeID) : null;
        return mentee as BuddyUserMentee;
      };
    },
  })
  mentee: Maybe<BuddyUserMentee>;

  @validate(nonOptional())
  @Field(type => ID)
  timeframeID: Maybe<string>;

  @Field(type => [MatchComment])
  comments: Maybe<MatchComment[]>;

  @Field(type => Date)
  createdAt: Maybe<Date>;

  static override async beforeCreate(match: Match) {
    await super.beforeCreate(match);

    match.createdAt = new Date();
  }

  static override async afterCreate(match: Match, result: InsertOneResult) {
    await super.afterCreate(match, result);
  }
}
